import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Divider, Grid, ListItem, ListItemText, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useHistory, useParams } from 'react-router';

import { DataAPIContext } from '../api';
import { UPDATE_BATCH } from '../api/dataProvider';
import useBatchDelete from '../bg_tasks/useBatchDelete';
import ConfirmationDialog from '../components/ConfirmationDialog';
import FixedLinearProgress from '../components/FixedLinearProgress';
import PageHeading from '../components/PageHeading';
import PageMeta from '../components/PageMeta';
import RecordList from '../components/RecordList';
import { getLicenseeColumn } from '../components/franchiseColumns';
import { ContextStore } from '../store/contextStore';
import RegionEdit from './RegionEdit';
import RegionsListToolbar from './RegionListToolbar';
import { ConfirmationDialogText } from './utils';

function useIsWidthUp(breakpoint) {
    const theme = useTheme();
    return useMediaQuery(theme.breakpoints.up(breakpoint));
}
const useStyles = makeStyles((theme) => ({
    root: {
        paddingTop: 0,
        marginTop: 0,
        margin: theme.spacing(2),
        marginBottom: 0,
        height: `calc(${theme.cp.contentHeight} - 1rem) !important`,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        boxSizing: 'border-box',
    },
    gridRoot: {
        flex: 1,
        minHeight: 0,
        width: '100%',
    },
    recordList: {
        marginTop: theme.spacing(2),
        height: `calc(${theme.cp.contentHeight} - 6rem) !important`,
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
    },
    sortButton: {
        minWidth: theme.spacing(4),
        minHeight: theme.spacing(4),
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        padding: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
    progress: {
        marginBottom: theme.spacing(1),
    },
}));

const RegionsList = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const classes = useStyles();
    const resource = 'region';
    const [query, setQuery] = React.useState(null);
    const [syncActive, setSyncActive] = React.useState(false);
    const [detailsDirty, setDetailsDirty] = React.useState(false);
    const [selectedIds, setSelectedIds] = React.useState([]);
    const [lastUpdate, setLastUpdate] = React.useState(null);
    const [newRowOrder, setNewRowOrder] = React.useState(null);
    const { enqueueSnackbar } = useSnackbar();
    const [confirmationDialogAction, setConfirmationDialogAction] = React.useState(null);
    const [idsToDelete, setIdsToDelete] = React.useState(null);
    const isSmallScreen = !useIsWidthUp('sm');
    const { hasPerm, isFranchise, licensees, currentLicensee } = React.useContext(ContextStore);

    const history = useHistory();
    const routeParams = useParams();
    const [activeRegionId, setActiveRegionId] = React.useState(routeParams.id);

    React.useEffect(() => {
        if (!routeParams.id) {
            setActiveRegionId(null);
        }
    }, [props]);

    const onQueryChange = (query) => {
        setQuery(query);
    };

    const onSelectionModelChange = (ids, selection) => {
        setSelectedIds(selection);
        if (selection.length === 1) {
            history.push(`/explore/regions/${selection[0]}/preview`);
            setActiveRegionId(selection[0]);
        } else {
            history.push(`/explore/regions`);
            setActiveRegionId(null);
        }
    };
    // CP-7771: bulk delete runs as a background task; the grid refreshes once it has finished.
    const refreshList = React.useCallback(() => setLastUpdate(new Date().toString()), []);
    const { deleteRecords } = useBatchDelete({ resource, names: ['region', 'regions'], onDeleted: refreshList });
    const doDeleteRegionList = (idlist) => {
        setSyncActive(true);
        deleteRecords(idlist)
            .then((accepted) => {
                if (accepted) {
                    setIdsToDelete(null);
                    history.push(`/explore/regions`);
                }
            })
            .finally(() => {
                setSelectedIds([]);
                setSyncActive(false);
            });
    };

    const onDeleteRegionList = (idlist) => {
        setIdsToDelete(idlist);
        setConfirmationDialogAction(idlist.length > 1 ? 'delete_regions' : 'delete_region');
    };

    const onRegionDelete = (f) => {
        return doDeleteRegionList([f.id]);
    };

    // const onSaveNewOrder = (ev) => {
    //     setSyncActive(true);
    //     dataAPI(UPDATE_BATCH, resource, { data: newRowOrder, urlSuffix: 'reorder/' })
    //         .then((res) => {
    //             console.log('reorder res', res);
    //             if (res.statusCode < 400) {
    //                 enqueueSnackbar('New region order saved', { variant: 'success' });
    //                 setLastUpdate(new Date().toString());
    //                 setNewRowOrder(null);
    //             } else {
    //                 enqueueSnackbar('Error saving new region order', { variant: 'error' });
    //             }
    //         })
    //         .catch((err) => {
    //             enqueueSnackbar('Error saving new region order', { variant: 'error' });
    //         })
    //         .finally(() => {
    //             setSyncActive(false);
    //         });
    // };

    // const onRowOrderUpdate = (upd) => {
    //     const newOrder = { ...newRowOrder, ...upd };
    //     console.log('newOrder', newOrder);

    //     console.log('XYZ upd', upd);
    //     console.log('XYZ new', newOrder);
    //     setNewRowOrder(newOrder);
    // };

    const onRowOrderUpdate = async (upd) => {
        // Drag & drop saves immediately — leaving the change pending until a separate
        // 'Save new order' click loses it to the next background refetch.
        if (upd.direction === 'drag' || Object.keys(upd).length === 2) {
            await onSaveNewOrder(false, { ...newRowOrder, ...upd });
            return;
        }
        const newOrder = { ...newRowOrder, ...upd };
        setNewRowOrder(newOrder);
    };

    const onSaveNewOrder = async (reset_order, instantSaveNewOrder) => {
        let rowOrder = instantSaveNewOrder || newRowOrder;
        if (reset_order) {
            setNewRowOrder(null);
            setLastUpdate(new Date().toString());
            enqueueSnackbar('New order canceled', { variant: 'default' });
        } else {
            setSyncActive(true);
            await dataAPI(UPDATE_BATCH, resource, { data: rowOrder, urlSuffix: 'reorder/' })
                .then((res) => {
                    console.log('reorder res', res);
                    if (res.statusCode < 400) {
                        setLastUpdate(new Date().toString());
                        setNewRowOrder(null);
                        enqueueSnackbar('New order saved', { variant: 'success' });
                    } else {
                        setLastUpdate(new Date().toString());
                        enqueueSnackbar('Error saving new order', { variant: 'error' });
                    }
                })
                .catch((err) => {
                    setLastUpdate(new Date().toString());
                    enqueueSnackbar('Error saving new order', { variant: 'error' });
                })
                .finally(() => {
                    setSyncActive(false);
                });
        }
    };

    const onRowClick = (p) => {
        if (!detailsDirty) {
            setActiveRegionId(p.row.id);
            history.push(`/explore/regions/${p.row.id}/preview`);
            setDetailsDirty(false);
        }
    };

    const onDetailsClose = (ev) => {
        setActiveRegionId(null);
        history.push('/explore/regions');
        setDetailsDirty(false);
    };

    const onRegionChange = (fn) => {
        setLastUpdate(new Date().toString());
    };

    const listItemRenderer = (row, idx, rows, gridApi) => {
        const accessTags = row.atag?.map((x) => x.name).join(', ');
        const hasLatLon = row.latitude && row.longitude;

        return (
            <React.Fragment>
                <ListItem alignItems="flex-start" className={classes.listItem} onClick={(ev) => gridApi.onRowClick({ row, idx }, ev)}>
                    <ListItemText
                        primary={row.name}
                        secondary={
                            <Grid container direction="row" spacing={1}>
                                <Grid item xs={6}>
                                    Sequence: {row.sequence}
                                </Grid>
                                <Grid item xs={6} container justifyContent="flex-end">
                                    {hasLatLon ? <CheckIcon fontSize="small" sx={{ mr: 0.5 }} /> : <CloseIcon fontSize="small" sx={{ mr: 0.5 }} />}
                                    lat/lon
                                </Grid>
                                {row.custom_id && (
                                    <Grid item xs={12}>
                                        External ID: {row.custom_id}
                                    </Grid>
                                )}
                                {accessTags && (
                                    <Grid item xs={12}>
                                        {accessTags}
                                    </Grid>
                                )}
                            </Grid>
                        }
                    />
                </ListItem>
                {idx < rows.length - 1 ? <Divider /> : null}
            </React.Fragment>
        );
    };

    const cols = isSmallScreen ? (activeRegionId ? 0 : 12) : activeRegionId ? 8 : 12;

    const confirmationDialogCancel = () => {
        setConfirmationDialogAction(null);
    };
    const confirmationDialogConfirm = () => {
        console.log('confirm', confirmationDialogAction);
        switch (confirmationDialogAction) {
            case 'delete_region':
            case 'delete_regions':
                doDeleteRegionList(idsToDelete);
                break;
            default:
                break;
        }
        confirmationDialogCancel();
    };

    return (
        <div className={classes.root}>
            <PageMeta title="Regions" />
            {confirmationDialogAction ? (
                <ConfirmationDialog
                    classes={{ paper: classes.paper }}
                    keepMounted
                    onCancel={confirmationDialogCancel}
                    onConfirm={confirmationDialogConfirm}
                    title={ConfirmationDialogText[confirmationDialogAction].title}
                    okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                    cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                    open={!!confirmationDialogAction}
                >
                    {ConfirmationDialogText[confirmationDialogAction].body}
                </ConfirmationDialog>
            ) : null}
            <FixedLinearProgress visibility={syncActive} />
            <Grid container direction="row" spacing={2} className={classes.gridRoot} alignItems="stretch">
                {isSmallScreen && activeRegionId ? null : (
                    <Grid item xs={cols} sx={{ height: '100%', pb: 0 }}>
                        <PageHeading sx={{ mt: 2, mb: 1.5 }}>Regions</PageHeading>
                        <RegionsListToolbar hasPerm={hasPerm} resource={resource} selectedIds={selectedIds} onQueryChange={onQueryChange} onDeleteRegion={onDeleteRegionList} newRowOrder={newRowOrder} onSaveNewOrder={onSaveNewOrder} isSmallScreen={isSmallScreen} />
                        <RecordList
                            className={classes.recordList}
                            resource={resource}
                            flavor="pro"
                            rowReordering={hasPerm('region', 'write')}
                            rowsPerPageOptions={[10, 25, 50, 100, 250, 500]}
                            disableSelectionOnClick={false}
                            onSelectionModelChange={onSelectionModelChange}
                            onRowClick={onRowClick}
                            disableColumnMenu={true}
                            deps={lastUpdate}
                            query={query}
                            rowHeight={100}
                            perPage={100}
                            onRowOrderUpdate={onRowOrderUpdate}
                            listItemRenderer={listItemRenderer}
                            isSmallScreen={isSmallScreen}
                            sortModel={[{ field: 'sequence', sort: 'asc' }]}
                            limitHeightForMobile={'calc(100vh - 9.5rem)'}
                            columns={[
                                ...getLicenseeColumn(isFranchise, licensees, currentLicensee),
                                { field: 'sequence', headerName: 'Sequence', type: 'reorder', sortable: false },
                                { field: 'name', headerName: 'Name', sortable: false, flex: 1 },
                                { field: 'custom_id', headerName: 'External ID', sortable: false, flex: 0.5 },
                                { field: 'atag', headerName: 'Access tags', sortable: false, flex: 0.5, valueGetter: (p) => p.row.atag?.map((x) => x.name).join(', ') },
                                {
                                    field: 'has_latlon',
                                    headerName: 'Has lat/lon',
                                    sortable: false,
                                    width: 100,
                                    align: 'center',
                                    headerAlign: 'center',
                                    renderCell: (p) => (p.row.latitude && p.row.longitude ? <CheckIcon fontSize="small" /> : <CloseIcon fontSize="small" />),
                                },
                            ]}
                        />
                    </Grid>
                )}
                {activeRegionId ? (
                    <Grid
                        item
                        xs={12 - cols}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 0,
                            height: '100%',
                            paddingBottom: '0px !important',
                        }}
                    >
                        <RegionEdit hasPerm={hasPerm} resource={resource} id={activeRegionId} onClose={onDetailsClose} onChange={onRegionChange} onDelete={onRegionDelete} syncActive={syncActive} setSyncActive={setSyncActive} setDetailsDirty={setDetailsDirty} />
                    </Grid>
                ) : null}
            </Grid>
        </div>
    );
};

export default RegionsList;
